import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Brain, CheckCircle2, XCircle, RotateCcw } from "lucide-react";
import { toast } from "sonner";

export default function QuizDialog({ open, onOpenChange, onDone }) {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setAnswers({}); setResult(null); setLoading(true);
    api.get("/quiz/questions").then(({ data }) => setQuestions(data)).finally(() => setLoading(false));
  }, [open]);

  const allAnswered = questions.length > 0 && questions.every((q) => answers[q.id] !== undefined);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/quiz/submit", { answers });
      setResult(data);
      onDone?.(data);
      toast.success(`Score : ${data.score}/${data.total} — Badge ${data.badge}`);
    } catch (e) {
      toast.error("Erreur lors de l'envoi du quiz");
    } finally { setBusy(false); }
  };

  const restart = () => { setAnswers({}); setResult(null); };

  let badgeColor = "bg-slate-500";
  if (result?.pct >= 80) badgeColor = "bg-green-600";
  else if (result?.pct >= 50) badgeColor = "bg-congo-gold text-slate-900";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-auto" data-testid="quiz-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2"><Brain className="h-5 w-5 text-congo-blue" /> Test connaissances investissements</DialogTitle>
          <DialogDescription>{result ? "Voici vos résultats et les explications." : "Répondez aux questions pour évaluer vos connaissances."}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-7 w-7 animate-spin text-congo-blue" /></div>
        ) : result ? (
          <div className="space-y-4" data-testid="quiz-result">
            <div className="text-center py-3">
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-white font-semibold ${badgeColor}`} data-testid="quiz-badge">
                {result.score}/{result.total} · Badge {result.badge}
              </div>
            </div>
            <div className="space-y-3">
              {questions.map((q) => {
                const corr = result.corrections.find((c) => c.id === q.id);
                const chosen = answers[q.id];
                return (
                  <div key={q.id} className="border border-slate-200 rounded-xl p-3">
                    <p className="font-medium text-sm text-slate-900 flex items-start gap-2">
                      {corr.correct ? <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" /> : <XCircle className="h-4 w-4 text-congo-red mt-0.5 shrink-0" />}
                      {q.question}
                    </p>
                    <p className="text-xs mt-1 text-slate-600">Bonne réponse : <span className="font-semibold text-slate-800">{q.options[corr.correct_index]}</span>{!corr.correct && chosen !== undefined && <span className="text-congo-red"> · votre réponse : {q.options[chosen]}</span>}</p>
                    <p className="text-xs mt-1 text-slate-500 italic">{corr.explanation}</p>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={restart} data-testid="quiz-restart-btn" className="flex-1 rounded-full"><RotateCcw className="h-4 w-4 mr-1" /> Refaire</Button>
              <Button onClick={() => onOpenChange(false)} className="flex-1 rounded-full bg-congo-blue" data-testid="quiz-close-btn">Terminer</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {questions.map((q, idx) => (
              <div key={q.id} data-testid={`quiz-q-${q.id}`}>
                <p className="font-medium text-sm text-slate-900">{idx + 1}. {q.question}</p>
                <div className="mt-2 space-y-1.5">
                  {q.options.map((opt, oi) => (
                    <label key={opt} className={`flex items-center gap-2 rounded-lg border p-2.5 cursor-pointer text-sm transition-colors ${answers[q.id] === oi ? "border-congo-blue bg-congo-bluelight" : "border-slate-200 hover:border-slate-300"}`} data-testid={`quiz-opt-${q.id}-${oi}`}>
                      <input type="radio" name={q.id} checked={answers[q.id] === oi} onChange={() => setAnswers({ ...answers, [q.id]: oi })} className="accent-congo-blue" />
                      {opt}
                    </label>
                  ))}
                </div>
              </div>
            ))}
            <Button onClick={submit} disabled={!allAnswered || busy} data-testid="quiz-submit-btn" className="w-full h-11 rounded-full bg-congo-blue">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Valider mes réponses"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
